import React, { useState, useEffect } from 'react';
import { AspectMapping } from 'services/categoryMappingService';
import GuidanceRow from './GuidanceRow';
import { stripCategoryPrefix } from './helpers';

interface AspectRowProps {
  aspect: AspectMapping;
  categoryName: string;
  rowNum: number;
  colSpan: number;
  isLast: boolean;
  expandAllToken: number;
  collapseAllToken: number;
}

/**
 * Renders a single aspect as a compact table row, with an inline toggle
 * that expands the GuidanceRow (1–10 score breakdown) beneath it.
 */
const AspectRow: React.FC<AspectRowProps> = ({
  aspect,
  categoryName,
  rowNum,
  colSpan,
  isLast,
  expandAllToken,
  collapseAllToken,
}) => {
  const [open, setOpen] = useState(false);
  const hasGuidance = Object.keys(aspect.scaleGuidance).length > 0;

  useEffect(() => {
    if (expandAllToken > 0 && hasGuidance) {
      setOpen(true);
    }
  }, [expandAllToken, hasGuidance]);

  useEffect(() => {
    if (collapseAllToken > 0) {
      setOpen(false);
    }
  }, [collapseAllToken]);

  const weightColour =
    aspect.weight >= 40
      ? 'text-red-600 font-bold'
      : aspect.weight >= 30
        ? 'text-orange-600 font-semibold'
        : 'text-blue-600';

  return (
    <>
      <tr
        id={`aspect-row-${aspect.documentId}`}
        className={`hover:bg-blue-50/40 transition-colors ${isLast ? '' : 'border-b border-gray-100'}`}
      >
        {/* Row # */}
        <td className="px-3 py-2 text-center text-xs text-gray-400 tabular-nums w-8">
          {rowNum}
        </td>

        {/* Aspect name */}
        <td className="px-3 py-2 text-xs font-semibold text-gray-800 font-baijamjuree w-72 break-words">
          {stripCategoryPrefix(aspect.name, categoryName)}
        </td>

        <td>
          <span className="text-gray-400 italic text-xs">
            (AspectID: {aspect.documentId})
          </span>
        </td>

        {/* Weight */}
        <td
          className={`px-3 py-2 text-xs text-center tabular-nums ${weightColour}`}
        >
          {aspect.weight}%
        </td>

        {/* Toggle */}
        <td className="px-3 py-2 text-right">
          <button
            id={`toggle-guidance-${aspect.documentId}`}
            aria-expanded={open}
            onClick={() => setOpen(v => !v)}
            disabled={!hasGuidance}
            className={`text-xs px-2 py-0.5 rounded border font-baijamjuree transition-colors ${
              open
                ? 'bg-indigo-50 border-indigo-200 text-indigo-600'
                : hasGuidance
                  ? 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-600'
                  : 'bg-gray-50 border-gray-100 text-gray-300 cursor-default'
            }`}
          >
            {open ? '▲ Hide Aspect' : '▼ Show Aspect'}
          </button>
        </td>
      </tr>

      {open && <GuidanceRow aspect={aspect} colSpan={colSpan} />}
    </>
  );
};

export default AspectRow;
